import React, { useEffect, useMemo, useState } from "react";
import { StepUpSpinner } from "../../../Components/Loader";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
  Box,
  Button,
  Card,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  useGetInvoiceQuery,
  useDeleteInvoiceMutation,
  useSendInvoiceMutation,
  useGetallStudentQuery,
} from "../../../Api/attoDeskApi";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import { useNotifier } from "../../../Core/Notifier";
import { useNavigate } from "react-router-dom";
import useUser from "../../../Core/hook/useUser";
import { useStyles } from "../../../theme/appStyles";
import DeleteOutlineSharpIcon from "@mui/icons-material/DeleteOutlineSharp";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import SendIcon from "@mui/icons-material/Send";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Pagination from "../../../Components/Pagination";
import useIsMobile from "../../../Core/hook/useIsMobile";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { canCreate } = useUser();
  const { showErrorMessage, showMessage } = useNotifier();
  const isMobileRes = useIsMobile();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [studentFilter, setStudentFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);

  const { data, isLoading, isError } = useGetInvoiceQuery({
    studentId: studentFilter,
    status: statusFilter,
    startDate: startDate ? startDate.format("YYYY-MM-DD") : "",
    endDate: endDate ? endDate.format("YYYY-MM-DD") : "",
  });

  const { data: studentData } = useGetallStudentQuery({ searchText: "", id: "" });
  const studentList = useMemo(() => Array.isArray(studentData?.data) ? (studentData?.data as any[]) : [], [studentData]);

  const invoices: any[] = useMemo(() => Array.isArray(data?.data) ? (data?.data as any[]) : [], [data]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const [deleteInvoice] = useDeleteInvoiceMutation();
  const [sendInvoice] = useSendInvoiceMutation();

  const handleDelete = async (id: string) => {
    try {
      const response = await deleteInvoice(id).unwrap();
      if (response.status) {
        showMessage(response.message);
        setOpenDeletePopup(false);
      } else {
        showErrorMessage(response.message);
      }
    } catch (error) {
      showErrorMessage("Failed to delete the invoice");
    }
  };

  const handleSend = async (invoiceId: string) => {
    try {
      const response = await sendInvoice({ invoiceId }).unwrap();
      if (response.status) {
        showMessage(response.message || "Invoice sent successfully");
      } else {
        showErrorMessage(response.message || "Failed to send invoice");
      }
    } catch (error) {
      showErrorMessage("Failed to send invoice");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case "paid": return "success";
      case "sent": return "primary";
      case "overdue": return "error";
      case "draft": return "default";
      case "partial": return "warning";
      default: return "default";
    }
  };

  return (
    <Card className={classes.card}>
      <Grid
        container
        justifyContent={"space-between"}
        spacing={2}
        sx={{ padding: 2 }}
        className={classes.titleGrid}
      >
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Typography className={classes.cardTitle}>Invoices</Typography>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ padding: 2 }}>
        <Grid item lg={2} md={3} sm={6} xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel>Student</InputLabel>
            <Select value={studentFilter} label="Student" onChange={(e) => setStudentFilter(e.target.value)}>
              <MenuItem value="">All Students</MenuItem>
              {studentList.map((s: any) => (
                <MenuItem key={s.id} value={s.id}>{s.firstName} {s.lastName}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item lg={2} md={3} sm={6} xs={12}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
              <MenuItem value="partial">Partial</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid item lg={2} md={3} sm={6} xs={12}>
            <DatePicker
              label="From"
              value={startDate}
              onChange={(val) => setStartDate(val)}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </Grid>
          <Grid item lg={2} md={3} sm={6} xs={12}>
            <DatePicker
              label="To"
              value={endDate}
              onChange={(val) => setEndDate(val)}
              slotProps={{ textField: { size: "small", fullWidth: true } }}
            />
          </Grid>
        </LocalizationProvider>

        <Grid item lg={4} md={12} sm={12} xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
          {canCreate("Invoice") && (
          <Button className={classes.addNewButton} onClick={() => navigate("/invoice/create")}>
            Create Invoice
          </Button>
          )}
        </Grid>
      </Grid>

      <Grid>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead className={classes.tableHeaderRow}>
              <TableRow>
                <TableCell className={classes.tablheader}><strong>Invoice #</strong></TableCell>
                <TableCell className={classes.tablheader}><strong>Student</strong></TableCell>
                {!isMobileRes && (
                  <>
                    <TableCell className={classes.tablheader}><strong>Issue Date</strong></TableCell>
                    <TableCell className={classes.tablheader}><strong>Due Date</strong></TableCell>
                  </>
                )}
                <TableCell className={classes.tablheader} sx={{ textAlign: "right" }}><strong>Total</strong></TableCell>
                <TableCell className={classes.tablheader}><strong>Status</strong></TableCell>
                <TableCell className={classes.tablheader} sx={{ textAlign: "right" }}><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    <Box display="flex" justifyContent="center" alignItems="center" height="10vh">
                      <StepUpSpinner size="medium" />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : isError || invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={20} align="center">
                    {isError ? "Error fetching data" : "No invoices found"}
                  </TableCell>
                </TableRow>
              ) : (
                invoices
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: any) => (
                    <TableRow key={row.id} className={classes.tableRowHover}>
                      <TableCell className={classes.tablCell}>{row.invoiceNumber || row.id}</TableCell>
                      <TableCell className={classes.tablCell}>
                        {row.studentName || `${row.studentFirstName || ""} ${row.studentLastName || ""}`}
                      </TableCell>
                      {!isMobileRes && (
                        <>
                          <TableCell className={classes.tablCell}>
                            {row.issueDate ? dayjs(row.issueDate).format("YYYY-MM-DD") : ""}
                          </TableCell>
                          <TableCell className={classes.tablCell}>
                            {row.dueDate ? dayjs(row.dueDate).format("YYYY-MM-DD") : ""}
                          </TableCell>
                        </>
                      )}
                      <TableCell className={classes.tablCell} sx={{ textAlign: "right" }}>
                        ${parseFloat(row.totalAmount || row.total || 0).toFixed(2)}
                      </TableCell>
                      <TableCell className={classes.tablCell}>
                        <Chip label={row.status || "Draft"} color={getStatusColor(row.status) as any} size="small" />
                      </TableCell>
                      <TableCell className={classes.tablCell}>
                        <Grid container spacing={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                          <Grid item>
                            <Tooltip title="View">
                              <Button
                                className={classes.editButtonList}
                                onClick={() => navigate(`/finance/invoices/${row.id}`)}
                              >
                                <VisibilityIcon className={classes.editListIcon} />
                              </Button>
                            </Tooltip>
                          </Grid>
                          <Grid item>
                            <Tooltip title="Send">
                              <Button
                                className={classes.editButtonList}
                                onClick={() => handleSend(row.id?.toString())}
                              >
                                <SendIcon className={classes.editListIcon} />
                              </Button>
                            </Tooltip>
                          </Grid>
                          <Grid item>
                            <Button
                              className={classes.deleteButtonList}
                              onClick={() => { setItemToDelete(row); setOpenDeletePopup(true); }}
                            >
                              <DeleteOutlineSharpIcon className={classes.editListIcon} />
                            </Button>
                          </Grid>
                        </Grid>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
          <Grid spacing={5} p={"18px"} py={4} justifyContent={"end"} container textAlign={"center"} alignContent={"center"} alignItems={"center"}>
            <Grid sx={{ textAlign: "end", justifyContent: "flex-end", alignItems: "flex-end" }}>
              <Pagination count={invoices.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} />
            </Grid>
          </Grid>
        </TableContainer>
      </Grid>

      {itemToDelete && (
        <DeletePopup
          open={openDeletePopup}
          handleCloseDelete={() => { setItemToDelete(null); setOpenDeletePopup(false); }}
          onConfirm={async () => { await handleDelete(itemToDelete.id.toString()); }}
          title="Delete Invoice"
          content={`Are you sure you want to delete invoice "${itemToDelete.invoiceNumber || itemToDelete.id}"?`}
        />
      )}
    </Card>
  );
};

export default Index;
